import { useCallback, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { HiChevronDown, HiOutlineGlobeAlt } from "react-icons/hi2";
import { BiHelpCircle } from "react-icons/bi";
import { FaSpinner } from "react-icons/fa";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Paragraph } from "../Paragraph";
import { Toggle } from "../Toggle";
import { Icon } from "../Icon";
import { ProfileOrb } from "../ProfileOrb";
import { SidebarItem } from "../Sidebar";
import { useAuth } from "../../context/AuthContext";
import { useUpdateUserDetails } from "../../data/useUpdateUserDetails";
import { useClickOutside } from "../../hooks/useClickOutside";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { ROUTE } from "../../routes";
import { spin } from "../animation";
import { TOPBAR_HEIGHT, SIDEBAR_WIDTH } from "../layoutConfig";

const Spinning = styled.span`
  display: inline-flex;
  animation: spin 1.5s linear infinite;
  ${spin};
`;

const slide = keyframes`
  from {
    left: -30%;
  }
  to {
    left: 100%;
  }
`;

/** Thin indeterminate bar along the bottom of the top bar while the
 * Division/Team selection is being saved. */
const UpdatingBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    width: 30%;
    height: 100%;
    background: ${({ theme }) => theme.colors.blue.primary};
    animation: ${slide} 1s ease-in-out infinite;
  }
`;

type ScopeDivision = { id: number; display_name: string };
type ScopeTeam = { id: number; display_name: string; division: number };

const CombinedScopeSelector = ({
  divisions,
  teams,
  activeDivisionIds,
  activeTeamIds,
  onToggleDivision,
  onToggleTeam,
  onToggleAll,
  isUpdating,
}: {
  divisions: ScopeDivision[];
  teams: ScopeTeam[];
  activeDivisionIds: number[];
  activeTeamIds: number[];
  onToggleDivision: (id: number, checked: boolean) => void;
  onToggleTeam: (id: number, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  isUpdating: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useClickOutside(
    ref,
    isOpen,
    useCallback(() => setIsOpen(false), [])
  );

  const allDivisionsSelected =
    divisions.length > 0 && activeDivisionIds.length === divisions.length;
  const allTeamsSelected =
    teams.length > 0 && activeTeamIds.length === teams.length;
  const allSelected = allDivisionsSelected && allTeamsSelected;

  const divisionLabel =
    divisions.length === 0
      ? "No Divisions"
      : allDivisionsSelected
      ? "All Divisions"
      : activeDivisionIds.length === 0
      ? "No Divisions"
      : `${activeDivisionIds.length} Division${
          activeDivisionIds.length === 1 ? "" : "s"
        }`;
  const teamLabel =
    teams.length === 0
      ? "No Teams"
      : allTeamsSelected
      ? "All Teams"
      : activeTeamIds.length === 0
      ? "No Teams"
      : `${activeTeamIds.length} Team${activeTeamIds.length === 1 ? "" : "s"}`;

  const teamsInDivision = (divisionId: number) =>
    teams.filter(({ division }) => division === divisionId);

  return (
    <Flex position="relative" ref={ref}>
      <Flex
        alignItems="center"
        gap="8"
        onClick={() => setIsOpen((v) => !v)}
        style={{ cursor: "pointer" }}
      >
        <Icon Type={HiOutlineGlobeAlt} color="white" size="18px" />
        <Paragraph color="white" m="0px" fontWeight="bold" fontSize={1}>
          {isUpdating ? "Updating view..." : `${divisionLabel}, ${teamLabel}`}
        </Paragraph>
        {isUpdating ? (
          <Spinning>
            <Icon Type={FaSpinner} color="white" size="14px" />
          </Spinning>
        ) : (
          <Icon Type={HiChevronDown} color="white" size="14px" />
        )}
      </Flex>

      {isOpen && (
        <Box
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            left: 0,
            background: "white",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            zIndex: 20,
            minWidth: "220px",
            maxHeight: "380px",
            overflowY: "auto",
          }}
          p={1}
        >
          {divisions.length === 0 ? (
            <Paragraph color="gray.50" fontSize={1} m="0px" p={1}>
              No divisions or teams assigned to you yet.
            </Paragraph>
          ) : (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                py={0}
                px={1}
                borderBottom="1px solid"
                borderBottomColor="gray.10"
              >
                <Paragraph m="0px" fontWeight="bold" fontSize={1}>
                  All
                </Paragraph>
                <Box
                  style={{
                    transform: "scale(0.7)",
                    transformOrigin: "right center",
                  }}
                >
                  <Toggle
                    isChecked={allSelected}
                    isDisabled={isUpdating}
                    onChange={(e) => onToggleAll(e.target.checked)}
                  />
                </Box>
              </Flex>
              {divisions.map((division) => (
                <Box
                  key={division.id}
                  py={0}
                  borderBottom="1px solid"
                  borderBottomColor="gray.10"
                >
                  <Flex
                    justifyContent="space-between"
                    alignItems="center"
                    px={1}
                    py={0}
                  >
                    <Paragraph m="0px" fontWeight="bold" fontSize={1}>
                      {division.display_name}
                    </Paragraph>
                    <Box
                      style={{
                        transform: "scale(0.7)",
                        transformOrigin: "right center",
                      }}
                    >
                      <Toggle
                        isChecked={activeDivisionIds.includes(division.id)}
                        isDisabled={isUpdating}
                        onChange={(e) =>
                          onToggleDivision(division.id, e.target.checked)
                        }
                      />
                    </Box>
                  </Flex>
                  {teamsInDivision(division.id).map((team) => (
                    <Flex
                      key={team.id}
                      justifyContent="space-between"
                      alignItems="center"
                      pl={2}
                      pr={1}
                      py={0}
                    >
                      <Paragraph m="0px" color="gray.60" fontSize={1}>
                        {team.display_name}
                      </Paragraph>
                      <Box
                        style={{
                          transform: "scale(0.7)",
                          transformOrigin: "right center",
                        }}
                      >
                        <Toggle
                          isChecked={activeTeamIds.includes(team.id)}
                          isDisabled={isUpdating}
                          onChange={(e) =>
                            onToggleTeam(team.id, e.target.checked)
                          }
                        />
                      </Box>
                    </Flex>
                  ))}
                  {teamsInDivision(division.id).length === 0 && (
                    <Paragraph color="gray.50" fontSize={1} pl={2} m="0px">
                      No teams in this division.
                    </Paragraph>
                  )}
                </Box>
              ))}
            </>
          )}
        </Box>
      )}
    </Flex>
  );
};

export const TopHeaderBar = () => {
  const { userDetails } = useAuth();
  const { mutate: updateUser } = useUpdateUserDetails();
  // Covers the save and the userDetails refetch after it, so the toggles
  // show the new state before they're re-enabled. Toggling is blocked
  // meanwhile: each toggle builds on the current active ids, so a second
  // click before the first lands would overwrite it with stale ones.
  const [isUpdating, setIsUpdating] = useState(false);

  const divisions = userDetails?.division ?? [];
  const activeDivisionIds = (userDetails?.active_division ?? []).map(
    ({ id }) => id
  );
  const teams = userDetails?.team ?? [];
  const activeTeamIds = (userDetails?.active_team ?? []).map(({ id }) => id);

  const persistActiveScope = (
    nextActiveDivisionIds: number[],
    nextActiveTeamIds: number[]
  ) => {
    if (!userDetails || isUpdating) return;

    setIsUpdating(true);
    updateUser(
      {
        id: userDetails.id,
        active_division: nextActiveDivisionIds,
        active_team: nextActiveTeamIds,
      },
      {
        onSuccess: async () => {
          // The active scope filters every list the user can see, so refetch
          // everything — not just userDetails — to refresh the current page.
          await queryClient.invalidateQueries();
          setIsUpdating(false);
        },
        onError: (err: any) => {
          setIsUpdating(false);
          showError(err, "A problem occurred while updating your view.");
        },
      }
    );
  };

  const teamIdsInDivision = (divisionId: number) =>
    teams.filter(({ division }) => division === divisionId).map(({ id }) => id);

  // A Division can't be active with zero active Teams under it, so the two
  // selectors have to cross-cascade rather than toggle independently.
  const toggleDivision = (divisionId: number, checked: boolean) => {
    const teamIds = teamIdsInDivision(divisionId);

    if (checked) {
      const alreadyHasActiveTeam = teamIds.some((id) =>
        activeTeamIds.includes(id)
      );
      persistActiveScope(
        [...activeDivisionIds, divisionId],
        alreadyHasActiveTeam || teamIds.length === 0
          ? activeTeamIds
          : [...activeTeamIds, ...teamIds]
      );
    } else {
      persistActiveScope(
        activeDivisionIds.filter((id) => id !== divisionId),
        activeTeamIds.filter((id) => !teamIds.includes(id))
      );
    }
  };

  const toggleAllDivisions = (checked: boolean) => {
    persistActiveScope(
      checked ? divisions.map(({ id }) => id) : [],
      checked ? teams.map(({ id }) => id) : []
    );
  };

  const toggleTeam = (teamId: number, checked: boolean) => {
    const team = teams.find(({ id }) => id === teamId);

    if (checked) {
      const nextDivisionIds =
        team && !activeDivisionIds.includes(team.division)
          ? [...activeDivisionIds, team.division]
          : activeDivisionIds;
      persistActiveScope(nextDivisionIds, [...activeTeamIds, teamId]);
    } else {
      const nextTeamIds = activeTeamIds.filter((id) => id !== teamId);

      // A Division can't stay active with zero active Teams — switch it off
      // too if this was the last one active under it.
      const remainingTeamsInDivision = team
        ? teamIdsInDivision(team.division).filter((id) =>
            nextTeamIds.includes(id)
          )
        : [];
      const nextDivisionIds =
        team && remainingTeamsInDivision.length === 0
          ? activeDivisionIds.filter((id) => id !== team.division)
          : activeDivisionIds;

      persistActiveScope(nextDivisionIds, nextTeamIds);
    }
  };

  return (
    <Flex
      bg="blue.100"
      height={`${TOPBAR_HEIGHT}px`}
      alignItems="center"
      justifyContent="space-between"
      px={4}
      style={{
        position: "fixed",
        top: 0,
        left: `${SIDEBAR_WIDTH}px`,
        right: 0,
        zIndex: 15,
      }}
    >
      <Flex alignItems="center" gap="32">
        <CombinedScopeSelector
          divisions={divisions}
          teams={teams}
          activeDivisionIds={activeDivisionIds}
          activeTeamIds={activeTeamIds}
          onToggleDivision={toggleDivision}
          onToggleTeam={toggleTeam}
          onToggleAll={toggleAllDivisions}
          isUpdating={isUpdating}
        />
      </Flex>

      <Flex alignItems="center">
        <SidebarItem
          tooltip="Help/Support"
          IconType={BiHelpCircle}
          to={ROUTE.HELP_AND_SUPPORT}
          mb={0}
        />

        <SidebarItem tooltip="Profile" to={ROUTE.PROFILE} mb={0}>
          <ProfileOrb
            firstName={userDetails?.first_name}
            lastName={userDetails?.last_name}
            colour={userDetails?.colour}
          />
        </SidebarItem>
      </Flex>

      {isUpdating && <UpdatingBar />}
    </Flex>
  );
};
